import type {
  AlterarPessoaParametroDto,
  AtualizarPessoaDto,
  CriarPessoaDto,
  CriarPessoaParametroDto,
  ExcluirPessoaParametroDto,
  ListarPessoasQuery,
  PessoaParametroResponse,
  PessoaResponse,
} from '@triade/contracts';
import { useConsulta, useMutacao } from './base';

/** Pessoas (colaboradores) da empresa (GET /pessoas): `rh.ver`. Sem a permissao a API responde 403; a tela nao consulta. */
export function usePessoas(q: Partial<ListarPessoasQuery> = {}) {
  return useConsulta<PessoaResponse[]>(['pessoas'], '/pessoas', { query: q });
}

/** Uma pessoa com o historico de parametros (GET /pessoas/:id). */
export function usePessoa(id: string | undefined) {
  return useConsulta<PessoaResponse>(['pessoas', id], `/pessoas/${id}`, { ativo: !!id });
}

export function useCriarPessoa() {
  return useMutacao<PessoaResponse, CriarPessoaDto>({
    metodo: 'POST',
    caminho: () => '/pessoas',
    corpo: (v) => v,
    invalidar: [['pessoas'], ['centros']],
  });
}

export function useAtualizarPessoa() {
  return useMutacao<PessoaResponse, { id: string } & AtualizarPessoaDto>({
    metodo: 'PATCH',
    caminho: (v) => `/pessoas/${v.id}`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['pessoas'], ['centros']],
  });
}

/** Nova vigencia de parametros (POST /pessoas/:id/parametros): fecha a anterior na API. */
export function useCriarParametroPessoa() {
  return useMutacao<PessoaParametroResponse, { pessoaId: string } & CriarPessoaParametroDto>({
    metodo: 'POST',
    caminho: (v) => `/pessoas/${v.pessoaId}/parametros`,
    corpo: ({ pessoaId: _pessoaId, ...resto }) => resto,
    invalidar: [['pessoas'], ['centros']],
  });
}

/** Corrige uma vigencia ja gravada (PATCH .../parametros/:parametroId): so ADMIN_EMPRESA, ADMIN_TENANT, CONSULTOR e SUPERADMIN. Motivo obrigatorio. */
export function useAlterarParametroPessoa() {
  return useMutacao<PessoaParametroResponse, { pessoaId: string; parametroId: string } & AlterarPessoaParametroDto>({
    metodo: 'PATCH',
    caminho: (v) => `/pessoas/${v.pessoaId}/parametros/${v.parametroId}`,
    corpo: ({ pessoaId: _pessoaId, parametroId: _parametroId, ...resto }) => resto,
    invalidar: [['pessoas'], ['centros']],
  });
}

/** Exclui uma vigencia (POST .../parametros/:parametroId/excluir): resposta 204 (sem corpo). Excluir a ultima reabre a anterior. */
export function useExcluirParametroPessoa() {
  return useMutacao<void, { pessoaId: string; parametroId: string } & ExcluirPessoaParametroDto>({
    metodo: 'POST',
    caminho: (v) => `/pessoas/${v.pessoaId}/parametros/${v.parametroId}/excluir`,
    corpo: ({ motivo }) => ({ motivo }),
    invalidar: [['pessoas'], ['centros']],
  });
}
